/**
 * Admin Splash editor — one splash per line (Enter). Supports interactive links:
 *   Try [mode.normal]Normal mode
 * Autocomplete appears while typing inside `[…]`.
 */
import { fetchAdminSplashTexts, putAdminSplashTexts } from "../lib/cloud";
import {
  filterSplashLinkTargets,
  splashAutocompleteQuery,
  type SplashLinkTarget,
} from "./splash-links";

export function mountAdminSplashEditor(root: HTMLElement): { refresh: () => void } {
  root.replaceChildren();

  const heading = document.createElement("h3");
  heading.textContent = "Splash";
  root.appendChild(heading);

  const hint = document.createElement("p");
  hint.className = "accessHint";
  hint.innerHTML =
    "One splash per line. Press Enter for a new line. Interactive links use " +
    "<code>[target]Label</code> — e.g. <code>Try [mode.normal]Normal</code>, " +
    "<code>[mode.colorrush]Color_Rush</code> (underscore = space in the link), " +
    "<code>Open [screen.shop]Shop</code>, or " +
    "<code>[settings.homesplashtexts]HERE!</code>. Type <code>[</code> for autocomplete.";
  root.appendChild(hint);

  const status = document.createElement("p");
  status.className = "accessHint";
  root.appendChild(status);

  const box = document.createElement("div");
  box.className = "modeBox adminSplashEditorBox";

  const editorWrap = document.createElement("div");
  editorWrap.className = "adminSplashEditorWrap";

  const ta = document.createElement("textarea");
  ta.className = "formInput adminSplashTextarea";
  ta.rows = 16;
  ta.spellcheck = true;
  ta.placeholder =
    "Also try WASD!\nTry [mode.normal]Normal mode\nDisable me [settings.homesplashtexts]HERE!";
  ta.setAttribute("aria-label", "Splash texts, one per line");
  ta.setAttribute("aria-autocomplete", "list");
  ta.setAttribute("aria-controls", "adminSplashSuggest");
  editorWrap.appendChild(ta);

  const suggest = document.createElement("div");
  suggest.id = "adminSplashSuggest";
  suggest.className = "adminSplashSuggest hidden";
  suggest.setAttribute("role", "listbox");
  suggest.setAttribute("aria-label", "Splash link targets");
  editorWrap.appendChild(suggest);

  box.appendChild(editorWrap);

  const actions = document.createElement("div");
  actions.className = "adminSplashActions";
  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.className = "btn green";
  saveBtn.textContent = "Save";
  const reloadBtn = document.createElement("button");
  reloadBtn.type = "button";
  reloadBtn.className = "btn";
  reloadBtn.textContent = "Reload";
  actions.appendChild(saveBtn);
  actions.appendChild(reloadBtn);
  box.appendChild(actions);
  root.appendChild(box);

  let busy = false;
  let activeIndex = 0;
  let currentMatches: SplashLinkTarget[] = [];
  let acStart = 0;

  function hideSuggest() {
    suggest.classList.add("hidden");
    suggest.replaceChildren();
    currentMatches = [];
    activeIndex = 0;
  }

  function renderSuggest(matches: SplashLinkTarget[]) {
    currentMatches = matches;
    if (!matches.length) {
      hideSuggest();
      return;
    }
    if (activeIndex >= matches.length) activeIndex = 0;
    suggest.replaceChildren();
    matches.forEach((m, i) => {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "adminSplashSuggestItem" + (i === activeIndex ? " adminSplashSuggestItem--active" : "");
      row.setAttribute("role", "option");
      row.setAttribute("aria-selected", i === activeIndex ? "true" : "false");
      const idSpan = document.createElement("code");
      idSpan.textContent = m.id;
      const lab = document.createElement("span");
      lab.textContent = m.label;
      row.appendChild(idSpan);
      row.appendChild(lab);
      row.onmousedown = (e) => {
        e.preventDefault();
        applySuggestion(m);
      };
      suggest.appendChild(row);
    });
    suggest.classList.remove("hidden");
  }

  function applySuggestion(target: SplashLinkTarget) {
    const start = acStart;
    const end = ta.selectionStart ?? start;
    const value = ta.value;
    const insert = `${target.id}]`;
    ta.value = value.slice(0, start) + insert + value.slice(end);
    const caret = start + insert.length;
    ta.focus();
    ta.setSelectionRange(caret, caret);
    hideSuggest();
  }

  function refreshAutocomplete() {
    const cursor = ta.selectionStart ?? 0;
    const info = splashAutocompleteQuery(ta.value, cursor);
    if (!info) {
      hideSuggest();
      return;
    }
    acStart = info.start;
    activeIndex = 0;
    renderSuggest(filterSplashLinkTargets(info.query));
  }

  ta.addEventListener("input", () => refreshAutocomplete());
  ta.addEventListener("click", () => refreshAutocomplete());
  ta.addEventListener("keyup", (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === "Escape") return;
    refreshAutocomplete();
  });
  ta.addEventListener("keydown", (e) => {
    if (suggest.classList.contains("hidden") || !currentMatches.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      activeIndex = (activeIndex + 1) % currentMatches.length;
      renderSuggest(currentMatches);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      activeIndex = (activeIndex - 1 + currentMatches.length) % currentMatches.length;
      renderSuggest(currentMatches);
      return;
    }
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const pick = currentMatches[activeIndex];
      if (pick) applySuggestion(pick);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      hideSuggest();
    }
  });
  ta.addEventListener("blur", () => {
    window.setTimeout(() => hideSuggest(), 120);
  });

  async function load() {
    if (busy) return;
    busy = true;
    status.textContent = "Loading…";
    saveBtn.disabled = true;
    reloadBtn.disabled = true;
    try {
      const data = await fetchAdminSplashTexts();
      ta.value = data.texts.join("\n");
      const n = data.texts.length;
      status.textContent = n
        ? `${n} splash${n === 1 ? "" : "es"} loaded.`
        : "No splashes yet — add lines and Save.";
    } catch (e) {
      status.textContent = e instanceof Error && e.message === "forbidden" ? "Forbidden." : "Could not load splashes.";
    } finally {
      busy = false;
      saveBtn.disabled = false;
      reloadBtn.disabled = false;
    }
  }

  async function save() {
    if (busy) return;
    busy = true;
    status.textContent = "Saving…";
    saveBtn.disabled = true;
    reloadBtn.disabled = true;
    try {
      const lines = ta.value
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      const data = await putAdminSplashTexts(lines);
      ta.value = data.texts.join("\n");
      const n = data.texts.length;
      status.textContent = `Saved ${n} splash${n === 1 ? "" : "es"}.`;
    } catch (e) {
      status.textContent = e instanceof Error && e.message === "forbidden" ? "Forbidden." : "Could not save splashes.";
    } finally {
      busy = false;
      saveBtn.disabled = false;
      reloadBtn.disabled = false;
    }
  }

  saveBtn.onclick = () => void save();
  reloadBtn.onclick = () => void load();
  void load();

  return { refresh: () => void load() };
}
